<script lang="ts">
export default {
  name: 'BenchmarkingTable',
};
</script>

<script setup lang="ts">
import type { ExposureMetric, ExposureValue, ExposureValueEntry } from '@/types/AccountExposure';
import { ref, computed, provide } from 'vue';
import AccountBenchmarkingTableRow from '@/views/account/benchmarking/AccountBenchmarkingTableRow.vue';
import AccountExposureEntryModal from '@/views/account/AccountExposureEntryModal.vue';
import FilterBar from '@/components/v2/FilterBar.vue';
import { Column } from './Benchmarking';
import { Benchmark } from '@/types/Benchmark';
import { accountService } from '@/services/api';
import { DownloadTableToCSV } from '@/utilities/Download';
import { DateTime } from 'luxon';
import { useRoute } from 'vue-router';
import Toggle from '@/components/v2/Toggle.vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { CalendarDaysIcon } from '@lucide/vue';

export interface ExposureModalPayload {
  exposureTypes: ExposureMetric[];
  canEnter: boolean;
  insert: (entries: ExposureValueEntry[]) => Promise<unknown>;
  exposureData: ExposureValue[];
  initialEffectiveDate: string;
  invalidEntryReason?: string;
}

const props = withDefaults(
  defineProps<{
    titleText: string;
    columns: Column[];
    loading: boolean;
    accountData: Benchmark[];
    otherData: Benchmark[];
    averageData: Benchmark[];
    medianData: Benchmark[];
    primaryPolicyEffectiveDate?: string;
  }>(),
  {
    primaryPolicyEffectiveDate: DateTime.now().toString(),
  },
);

const emit = defineEmits<{
  'needs-update': [];
}>();

const $route = useRoute();
const showVisuals = ref(false);
const countOfData = computed(() => props.otherData.length);

const exposureModalPayload = ref<ExposureModalPayload | null>(null);

function openExposureModal(payload: ExposureModalPayload) {
  exposureModalPayload.value = payload;
}

function closeExposureModal() {
  exposureModalPayload.value = null;
}

provide('openExposureModal', openExposureModal);

async function downloadTable() {
  const account = await accountService.findById($route.params.id as string);
  DownloadTableToCSV('#benchmarking-data', account.accountName + `-${new Date().toISOString().split('T')[0]}`);
}
</script>

<template>
  <div class="dashboard">
    <div>
      <FilterBar class="filter-bar">
        <slot name="filterbar" />
      </FilterBar>

      <div
        v-if="loading"
        class="space-y-2 p-2">
        <Skeleton
          v-for="i in 8"
          :key="i"
          shape="rectangle"
          class="w-full"
          height="44px" />
      </div>

      <div
        class="inner"
        v-if="!loading">
        <AccountExposureEntryModal
          v-if="exposureModalPayload"
          :show="true"
          :exposure-types="exposureModalPayload.exposureTypes"
          :can-enter="exposureModalPayload.canEnter"
          :insert="exposureModalPayload.insert"
          :exposure-data="exposureModalPayload.exposureData"
          :initial-effective-date="exposureModalPayload.initialEffectiveDate"
          :invalid-entry-reason="exposureModalPayload.invalidEntryReason ?? ''"
          @closed="closeExposureModal">
          <template #button><span /></template>
        </AccountExposureEntryModal>

        <div class="mb-1.5 flex content-start items-start gap-x-2">
          <div class="my-auto flex w-full items-center justify-between">
            <div class="flex items-center gap-2">
              <h2>{{ titleText }}</h2>
              <div
                class="flex items-center gap-2"
                :class="!showVisuals ? '' : 'invisible'">
                <DownloadButton @click="downloadTable" />
                <div class="badge badge-outline badge-primary">{{ countOfData }} Peer Programs</div>
              </div>
            </div>
            <Toggle
              v-model="showVisuals"
              label="Show Visuals"
              id="show-visuals" />
          </div>
        </div>
        <slot
          name="charts"
          v-if="showVisuals" />

        <div class="table-hscroller">
          <table
            v-if="!showVisuals"
            v-tablesort
            id="benchmarking-data"
            class="apptable sortable dense mt-4">
            <thead>
              <tr>
                <td
                  v-for="column in columns"
                  :key="column.fieldName"
                  :data-test="column.fieldName"
                  :class="`${column.type} ${column.classes || ''}`"
                  :title="`${column.title}${column.annualized ? ' (Annualized)' : ''}`">
                  {{ column.title }}
                  <CalendarDaysIcon
                    v-if="column.annualized"
                    :size="16"
                    class="mb-0.5 inline" />
                </td>
              </tr>
            </thead>

            <tbody>
              <AccountBenchmarkingTableRow
                v-for="d in accountData"
                :key="`acc-${d.policyYear}-${d.accountId}`"
                highlight
                frozen
                :data="d"
                :columns="columns"
                @needs-update="emit('needs-update')" />

              <tr class="rule notcsv frozen h-0.5">
                <td :colspan="columns.length"></td>
              </tr>

              <AccountBenchmarkingTableRow
                :static="true"
                v-for="d in averageData"
                :key="d.key"
                :data="d"
                :columns="columns"
                highlight
                shaded
                frozen />
              <AccountBenchmarkingTableRow
                :static="true"
                v-for="d in medianData"
                :key="d.key"
                :data="d"
                :columns="columns"
                highlight
                shaded
                frozen />

              <tr class="rule notcsv frozen h-0.5">
                <td :colspan="columns.length"></td>
              </tr>

              <AccountBenchmarkingTableRow
                v-for="d in otherData"
                :key="`oth-${d.policyYear}-${d.accountId}`"
                :data="d"
                :columns="columns"
                @needs-update="$emit('needs-update')" />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

